import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import TableV2 from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";

/** ---------------------- Tipos ---------------------- */

type ProdutoModo = "estoque" | "comercial";

type RequestMode = "template-post" | "legacy-get" | "commercial-get";

type ProdutoConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  modo: ProdutoModo;
  requestMode: RequestMode;

  departamento: string;
  awaitDepartamento: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;

  detalheProdutoPath: (codigo: string) => string;
};

type Produto = {
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;

  custo_unitario: number | string | null;
  moeda?: string | null;

  quantidade?: number | string | null;
  quantidade_atual?: number | string | null;

  custo_compra?: number | string | null;
  margem_percentual?: number | string | null;
  preco_venda?: number | string | null;

  ocultar?: boolean;
  vendavel?: boolean;
};

type ProdutoRaw = {
  codigo?: string;
  ncm?: string;
  nome?: string;
  classificacao?: string;
  posicao?: string;

  custo_unitario?: number | string | null;
  custo_unitario_atual?: number | string | null;
  custo_compra?: number | string | null;
  custo?: number | string | null;

  moeda?: string | null;

  quantidade?: number | string | null;
  qtd?: number | string | null;
  quantidade_atual?: number | string | null;
  estoque_atual?: {
    quantidade_atual?: number | string | null;
    custo_unitario_atual?: number | string | null;
    moeda?: string | null;
  } | null;

  margem_percentual?: number | string | null;
  margem?: number | string | null;

  preco_venda?: number | string | null;
  preco?: number | string | null;

  ocultar?: boolean;
  vendavel?: boolean;
};

type ListaResp = {
  fonte?: "cache" | "banco";
  versao?: number | null;
  data: Produto[];
};

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

/** ---------------------- Constantes ---------------------- */

const URL_TEMPLATE_GET =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-produto";

const API_BASE = "https://api.eva-googlemarine.com";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Configs antigas + fallback dinâmico ---------------------- */

const PRODUTOS_CONFIGS: ProdutoConfig[] = [
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-e-comercial",
    key: "suprimentos-servico-comercial",
    modo: "estoque",
    requestMode: "template-post",
    departamento: "suprimentos",
    awaitDepartamento: "suprimentos",
    title: "Suprimentos - Produtos",
    reportTitle: "Relatório de Produtos - Suprimentos",
    fileNamePrefix: "Suprimentos-Produtos",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-servico-e-comercial/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-campos",
    key: "suprimentos-campos",
    modo: "estoque",
    requestMode: "template-post",
    departamento: "suprimentos-campos",
    awaitDepartamento: "suprimentos-campos",
    title: "Suprimentos - Produtos registrados no estoque p/ Serviço Campos",
    reportTitle: "Relatório de Produtos - Suprimentos Campos",
    fileNamePrefix: "Suprimentos-Campos-Produtos",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-servico-campos/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-uso-coorporativo",
    key: "recursos-humanos",
    modo: "estoque",
    requestMode: "template-post",
    departamento: "recursos-humanos",
    awaitDepartamento: "recursos-humanos",
    title: "Suprimentos - Produtos do Estoque p/ Uso Coorporativo",
    reportTitle: "Relatório de Produtos - Recursos Humanos",
    fileNamePrefix: "Recursos-Humanos-Produtos",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-uso-coorporativo/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-tecnicos-e-laboratorio",
    key: "tecnicos",
    modo: "estoque",
    requestMode: "legacy-get",
    departamento: "tecnicos",
    awaitDepartamento: "tecnicos",
    title: "Laboratório - Produtos",
    reportTitle: "Relatório de Produtos - Laboratório",
    fileNamePrefix: "Laboratorio-Produtos",
    detalheProdutoPath: (codigo) =>
      `/suprimentos/estoque-p-tecnicos-e-laboratorio/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
  {
    areaSlug: "comercial",
    estoqueSlug: "materiais-p-venda",
    key: "comercial",
    modo: "comercial",
    requestMode: "commercial-get",
    departamento: "comercial",
    awaitDepartamento: "comercial",
    title: "Comercial - Produtos",
    reportTitle: "Relatório de Produtos - Comercial",
    fileNamePrefix: "Comercial-Produtos",
    detalheProdutoPath: (codigo) =>
      `/comercial/materiais-p-venda/produtos/${encodeURIComponent(codigo)}`,
  },
  {
    areaSlug: "coordenacao",
    estoqueSlug: "coordenacao-rastreio-de-materiais",
    key: "coordenacao",
    modo: "estoque",
    requestMode: "template-post",
    departamento: "coordenacao",
    awaitDepartamento: "coordenacao",
    title: "Coordenação - Produtos",
    reportTitle: "Relatório de Produtos - Coordenação",
    fileNamePrefix: "Coordenacao-Produtos",
    detalheProdutoPath: (codigo) =>
      `/coordenacao/coordenacao-rastreio-de-materiais/produtos/${encodeURIComponent(
        codigo
      )}`,
  },
];

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): ProdutoConfig {
  const area = normalizeSlug(areaSlug);
  const estoque = normalizeSlug(estoqueSlug);

  const found = PRODUTOS_CONFIGS.find(
    (config) => config.areaSlug === area && config.estoqueSlug === estoque
  );

  if (found) return found;

  const safeArea = area || "suprimentos";
  const safeEstoque = estoque || "estoque";

  const titleFromSlug = slugToTitle(safeEstoque);
  const isComercial = safeArea === "comercial";

  return {
    areaSlug: safeArea,
    estoqueSlug: safeEstoque,
    key: `${safeArea}-${safeEstoque}`,
    modo: isComercial ? "comercial" : "estoque",
    requestMode: isComercial ? "commercial-get" : "template-post",

    /**
     * Para estoques criados pelo cliente, o ideal é o backend filtrar também
     * por estoque_slug. Enquanto isso, o departamento fica como o próprio slug.
     */
    departamento: isComercial ? "comercial" : safeEstoque,
    awaitDepartamento: isComercial ? "comercial" : safeEstoque,

    title: `${slugToTitle(safeArea)} - Produtos - ${titleFromSlug}`,
    reportTitle: `Relatório de Produtos - ${titleFromSlug}`,
    fileNamePrefix: `${safeArea}-${safeEstoque}-Produtos`,

    detalheProdutoPath: (codigo) =>
      `/${safeArea}/${safeEstoque}/produtos/${encodeURIComponent(codigo)}`,
  };
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function produtosMudaram(a: Produto[], b: Produto[]): boolean {
  if (a.length !== b.length) return true;
  return JSON.stringify(a) !== JSON.stringify(b);
}

function toNumberSafe(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;

  if (typeof v === "number") {
    return Number.isFinite(v) ? v : null;
  }

  const s = String(v).trim();

  if (!s) return null;

  if (/[.,]/.test(s)) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");

    if (lastComma > lastDot) {
      const n = Number(s.replace(/\./g, "").replace(",", "."));
      return Number.isFinite(n) ? n : null;
    }

    if (lastDot > lastComma) {
      const n = Number(s.replace(/,/g, ""));
      return Number.isFinite(n) ? n : null;
    }
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function resolveCurrencyCode(moeda?: string | null): string {
  if (!moeda) return "BRL";

  const m = String(moeda).trim().toUpperCase();

  if (m === "BRL" || m === "R$") return "BRL";
  if (m === "USD" || m === "US$" || m === "$") return "USD";
  if (m === "EUR" || m === "€") return "EUR";

  return m || "BRL";
}

function currencySymbol(moeda?: string | null): string {
  const m = resolveCurrencyCode(moeda);

  if (m === "USD") return "$";
  if (m === "EUR") return "€";
  if (m === "BRL") return "R$";

  return "";
}

function formatCostWithSymbol(
  valor: number | string | null | undefined,
  moeda?: string | null
): string {
  if (
    valor === null ||
    valor === undefined ||
    valor === "" ||
    Number.isNaN(Number(String(valor).replace(",", ".")))
  ) {
    return "N/A";
  }

  const sym = currencySymbol(moeda);

  return `${sym ? sym + " " : ""}${valor}`;
}

function formatCurrency(valor: unknown, moeda?: string | null): string {
  if (valor === null || valor === undefined || valor === "") return "—";

  const n =
    typeof valor === "number" ? valor : Number(String(valor).replace(",", "."));

  if (!Number.isFinite(n)) return "—";

  const currency = resolveCurrencyCode(moeda);

  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(n);
  }
}

function formatPercent(valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return "—";

  const n =
    typeof valor === "number" ? valor : Number(String(valor).replace(",", "."));

  if (!Number.isFinite(n)) return "—";

  return `${n.toFixed(2)}%`;
}

function normalizeProduto(raw: ProdutoRaw): Produto {
  const estoqueAtual = raw.estoque_atual ?? null;

  const quantidadeRaw =
    raw.quantidade_atual ??
    raw.quantidade ??
    raw.qtd ??
    estoqueAtual?.quantidade_atual ??
    null;

  const custoRaw =
    raw.custo_unitario_atual ??
    raw.custo_unitario ??
    estoqueAtual?.custo_unitario_atual ??
    raw.custo_compra ??
    raw.custo ??
    null;

  const moedaRaw = raw.moeda ?? estoqueAtual?.moeda ?? null;

  const margemRaw = raw.margem_percentual ?? raw.margem ?? null;
  const precoVendaRaw = raw.preco_venda ?? raw.preco ?? null;

  return {
    codigo: String(raw.codigo ?? "").toUpperCase(),
    ncm: String(raw.ncm ?? ""),
    nome: String(raw.nome ?? "").toUpperCase(),
    classificacao: String(raw.classificacao ?? "").toUpperCase(),
    posicao: String(raw.posicao ?? "").toUpperCase(),

    custo_unitario: custoRaw,
    moeda: moedaRaw,

    quantidade: toNumberSafe(quantidadeRaw),
    quantidade_atual: toNumberSafe(quantidadeRaw),

    custo_compra: toNumberSafe(custoRaw),
    margem_percentual: toNumberSafe(margemRaw),
    preco_venda: toNumberSafe(precoVendaRaw),

    ocultar: raw.ocultar,
    vendavel: raw.vendavel,
  };
}

function onlyVisibleProdutos(rawList: ProdutoRaw[]): Produto[] {
  return rawList
    .filter((p) => p?.ocultar !== true)
    .map((p) => normalizeProduto(p));
}

/** ---------------------- HTTP helpers ---------------------- */

function getProdutosUrl(config: ProdutoConfig): string {
  if (config.requestMode === "commercial-get") {
    return `${API_BASE}/departamentos/comercial/produtos-comercial`;
  }

  if (config.requestMode === "legacy-get") {
    return `${API_BASE}/departamentos/${encodeURIComponent(
      config.departamento
    )}/produtos`;
  }

  return URL_TEMPLATE_GET;
}

function getAwaitUrl(config: ProdutoConfig, since: number): string {
  const params = new URLSearchParams();

  params.set("since", String(since));
  params.set("timeout_ms", String(AWAIT_TIMEOUT_MS));
  params.set("estoque_slug", config.estoqueSlug);

  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.awaitDepartamento
  )}/produtos/await?${params.toString()}`;
}

async function headVersao(
  config: ProdutoConfig,
  signal?: AbortSignal
): Promise<number | null> {
  try {
    if (config.requestMode === "template-post") return null;

    const res = await fetch(getProdutosUrl(config), {
      method: "HEAD",
      headers: buildAuthHeaders(false),
      signal,
    });

    if (!res.ok) return null;

    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;

    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  config: ProdutoConfig,
  _versaoAtual: number | null,
  signal?: AbortSignal
): Promise<ListaResp> {
  const url = getProdutosUrl(config);

  const res =
    config.requestMode === "template-post"
      ? await fetch(url, {
          method: "POST",
          headers: buildAuthHeaders(true),
          cache: "no-store",
          signal,
          body: JSON.stringify({
            departamento: config.departamento,
            estoque_slug: config.estoqueSlug,
            estoqueSlug: config.estoqueSlug,
          }),
        })
      : await fetch(url, {
          method: "GET",
          headers: buildAuthHeaders(false),
          cache: "no-store",
          signal,
        });

  if (!res.ok) {
    throw new Error(`Erro HTTP ${res.status}`);
  }

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;

  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = await res.json();

  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr as number) ? verHdr : verBody;

  const visiveisRaw: ProdutoRaw[] | undefined = Array.isArray(json?.visiveis)
    ? (json.visiveis as ProdutoRaw[])
    : undefined;

  const dataRaw: ProdutoRaw[] =
    visiveisRaw ??
    (Array.isArray(json?.data) ? (json.data as ProdutoRaw[]) : []);

  const data = onlyVisibleProdutos(dataRaw);

  return {
    fonte,
    versao: versao ?? null,
    data,
  };
}

async function awaitChange(
  config: ProdutoConfig,
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const res = await fetch(getAwaitUrl(config, since), {
      method: "GET",
      headers: buildAuthHeaders(false),
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();

    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;

    const changed = Boolean(json?.changed);

    return {
      changed,
      versao,
    };
  } catch {
    return null;
  }
}

/** ---------------------- Hook genérico ---------------------- */

function useProdutosDisponiveisGenerico(config: ProdutoConfig) {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, setVersaoState] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);

  const fetchingRef = useRef(false);
  const fetchCtrlRef = useRef<AbortController | null>(null);

  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitInFlightRef = useRef(false);
  const awaitSupported = useRef<boolean | null>(null);

  const setVersao = (v: number | null) => {
    versaoRef.current = v;
    setVersaoState(v);
  };

  async function carregarLista(cur: number | null, signal?: AbortSignal) {
    fetchingRef.current = true;

    const ac = signal ? null : new AbortController();

    if (ac) {
      fetchCtrlRef.current = ac;
    }

    try {
      setErro(null);

      const lista = await getLista(config, cur, signal ?? ac?.signal);

      if (lista.versao != null) {
        setVersao(lista.versao);
      }

      setProdutos((prev) =>
        produtosMudaram(prev, lista.data) ? lista.data : prev
      );

      setFonte(lista.fonte);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar produtos.");
        throw e;
      }
    } finally {
      if (ac && fetchCtrlRef.current === ac) {
        fetchCtrlRef.current = null;
      }

      fetchingRef.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    setLoading(true);
    setErro(null);
    setProdutos([]);
    setVersao(null);

    awaitSupported.current = null;
    fetchCtrlRef.current?.abort();
    awaitCtrlRef.current?.abort();

    const bootstrapController = new AbortController();

    (async () => {
      try {
        const head = await headVersao(config, bootstrapController.signal);

        if (head !== null) {
          setVersao(head);
        }

        await carregarLista(head, bootstrapController.signal);
        setLoading(false);

        const init = await awaitChange(config, -1);

        if (init && typeof init.versao === "number") {
          setVersao(init.versao);
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetchingRef.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur !== null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;

            try {
              resp = await awaitChange(config, cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(ERROR_BACKOFF_MS);
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }

              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              setVersao(resp.versao ?? cur);
              await carregarLista(resp.versao ?? cur);
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregarLista(versaoRef.current);
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useProdutosDisponiveisGenerico error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      bootstrapController.abort();
      fetchCtrlRef.current?.abort();
      awaitCtrlRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.key, config.areaSlug, config.estoqueSlug, config.departamento]);

  return {
    produtos,
    loading,
    fonte,
    versao,
    erro,
  };
}

/** ---------------------- Página ---------------------- */

const ProdutosGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const navigate = useNavigate();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const { produtos, loading, erro } = useProdutosDisponiveisGenerico(config);

  const isComercial = config.modo === "comercial";

  const headers = useMemo(() => {
    if (isComercial) {
      return [
        "Código",
        "NCM",
        "Nome do Produto",
        "Quantidade",
        "Custo de Compra",
        "Margem",
        "Preço de Venda",
      ];
    }

    return [
      "Código",
      "NCM",
      "Nome do Produto",
      "Custo Unitário",
      "Moeda",
      "Classificação",
      "Posição",
    ];
  }, [isComercial]);

  const columnWidths = useMemo(() => {
    if (isComercial) {
      return ["100px", "60px", "270px", "80px", "110px", "80px", "120px"];
    }

    return ["100px", "60px", "270px", "120px", "70px", "20px", "10px"];
  }, [isComercial]);

  const rows = useMemo(() => {
    if (isComercial) {
      return produtos.map((p) => [
        p.codigo,
        p.ncm,
        p.nome,
        p.quantidade ?? p.quantidade_atual ?? "—",
        formatCurrency(p.custo_compra ?? p.custo_unitario, p.moeda),
        formatPercent(p.margem_percentual),
        formatCurrency(p.preco_venda, p.moeda),
      ]);
    }

    return produtos.map((p) => [
      p.codigo,
      p.ncm,
      p.nome,
      formatCostWithSymbol(p.custo_unitario, p.moeda),
      (p.moeda || "").toUpperCase() || "—",
      p.classificacao,
      p.posicao,
    ]);
  }, [produtos, isComercial]);

  const enabledColumnIndexes = useMemo(
    () => headers.map((_h, i) => i),
    [headers]
  );

  const firstCode = rows.length ? rows[0][0] : "—";
  const lastCode = rows.length ? rows[rows.length - 1][0] : "—";
  const nextCode = `PRD001${(produtos.length + 1).toString().padStart(2, "0")}`;

  const gerarRelatorioProdutos = useCallback(() => {
    generatePdfReport({
      reportTitle: config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações dos produtos registrados",
      headers,
      rows,
      custoColIndex: isComercial ? 4 : 3,
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        rows.length
          ? `Códigos Utilizados: ${firstCode} - ${lastCode}`
          : "Sem produtos",
        `Próximo Código Disponível: ${nextCode}`,
      ],
    });
  }, [
    config.reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    isComercial,
    enabledColumnIndexes,
    firstCode,
    lastCode,
    nextCode,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        departamento?: {
          id?: number | string | null;
          name?: string;
          slug?: string | null;
          routeKey?: string;
        };
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const base = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/produtos`
      );

      const isProdutosContext =
        currentPath.includes(base) ||
        eventPath.includes(base) ||
        currentPath.includes("produtos") ||
        eventPath.includes("produtos") ||
        moduloLabel.includes("produtos") ||
        moduloCodigo.includes("produtos") ||
        moduloSlug.includes("produtos");

      if (!isProdutosContext) return;

      gerarRelatorioProdutos();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioProdutos]);

  const getCellStyle = useCallback(
    (
      _rowIndex: number,
      _colIndex: number,
      _value: string | number | React.ReactNode,
      originalIndex: number
    ): React.CSSProperties | undefined => {
      if (!isComercial) return undefined;

      const produto = produtos[originalIndex];

      if (!produto) return undefined;

      const margem = toNumberSafe(produto.margem_percentual);

      if (margem === 0) {
        return {
          backgroundColor: "#FF47474C",
        };
      }

      return undefined;
    },
    [isComercial, produtos]
  );

  const codigoIdx = headers.indexOf("Código");

  return (
    <div>
      <TitleButtons fullBleedLine title={config.title} buttons={[]} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando produtos…</div>
      ) : (
        <TableV2
          enableZebra
          hoverHighlight
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          getCellStyle={isComercial ? getCellStyle : undefined}
          onDoubleClick={(_viewIndex, rowData) => {
            const codigo = String(rowData[codigoIdx] ?? "").trim();

            if (!codigo) return;

            navigate(config.detalheProdutoPath(codigo));
          }}
        />
      )}
    </div>
  );
};

export default ProdutosGenerico;
